import { h, Component } from 'preact';
import { route } from 'preact-router';

import './index.less';

import I18n from '../../lib/I18n';
import EventBus from '../../lib/EventBus';
import { getOrder, getInquiryCount } from '../../lib/Order';
import { cdnHost, SEND_INQUIRY_ANIMATION_TIME } from '../../../config';
import { animate } from '../../lib/Animation';
import { onReviewActionClick } from '../../lib/Events';
import { getCatalogueTheme } from '../../lib/Amalgam';
import CartIcon from '../../molecules/Icon/CartIcon';
import ArrowUpIcon from '../../molecules/Icon/ArrowUpIcon';
import {
  getCurrentRouteName,
  isResellerViewRoute,
  shouldShowLightThemeOnRoute
} from '../../lib/Route';
import { onUpdateCartNativeHandler } from '../../../../../nativeConnectors/platform';
import { clearCachedScrollPositions } from '../../lib/ScrollPosition';
import { getReviewRoute } from '../../lib/RoutePath';
import { shouldProcessClick } from '../../lib/Utils';

class NavBarReviewAction extends Component {
  state = {
    loading: true,
    isVisible: false,
    popoverVisible: false,
    inquiryCount: 0,
    pulse: false
  };

  isPopoverVisible = false;
  shutdownTimer = null;
  timer = null;
  animationEndTimer = null;

  componentDidMount () {
    this.populateState();

    EventBus.addListener(EventBus.TYPE.ORDER_CHANGED, this.onOrderChanged);
    EventBus.addListener(EventBus.TYPE.ORDER_FINALIZED, this.onOrderFinalized);
    EventBus.addListener(EventBus.TYPE.ORDER_DELETED, this.onOrderDeleted);
    EventBus.addListener(EventBus.TYPE.ORDER_DELETED, this.onInquiryDeleted);
  }

  componentDidUpdate (prevProps, prevState) {
    if (prevState.isVisible !== this.state.isVisible) {
      if (typeof this.props.onVisibilityChanged === 'function') {
        this.props.onVisibilityChanged(this.state.isVisible);
      }
    }

    if (this.state.isVisible) {
      this.orderChangedHandler();
    }
  }

  componentWillUnmount () {
    EventBus.removeListener(EventBus.TYPE.ORDER_CHANGED, this.onOrderChanged);
    EventBus.removeListener(EventBus.TYPE.ORDER_FINALIZED, this.onOrderFinalized);
    EventBus.removeListener(EventBus.TYPE.ORDER_DELETED, this.onOrderDeleted);
    EventBus.removeListener(EventBus.TYPE.ORDER_DELETED, this.onInquiryDeleted);

    if (this.animationEndTimer) {
      clearTimeout(this.animationEndTimer);
    }
  }

  populateState = () => {
    if (this.state.inquiryCount) {
      this.setState({
        inquiryCount: 0,
        isVisible: false,
        loading: false
      });
      return;
    }

    getOrder().then((order) => {
      if (!order || this.state.inquiryCount) {
        this.setState({
          inquiryCount: 0,
          isVisible: !!order
        });
        return;
      }

      const inquiryCount = getInquiryCount(order.selectedProducts);

      this.setState({ inquiryCount, isVisible: true });
    });
  };

  onOrderChanged = () => {
    if (this.timer) {
      clearTimeout(this.timer);
      this.timer = null;
    }

    this.timer = setTimeout(() => {
      getOrder().then((order) => {
        const inquiryCount = order ? getInquiryCount(order.selectedProducts) : 0;

        this.setState({
          inquiryCount,
          isVisible: !!order
        });

        if (this.state.inquiryCount !== inquiryCount) {
          this.orderChangedHandler();
          this.showPopover();
          this.pulse();
        }
      });
    }, SEND_INQUIRY_ANIMATION_TIME);
  };

  onOrderFinalized = () => {};

  onOrderDeleted = () => {};

  onInquiryDeleted = () => {};

  startShutdownTimer = () => {
    if (this.shutdownTimer) {
      clearTimeout(this.shutdownTimer);
    }

    this.shutdownTimer = setTimeout(() => {
      this.isPopoverVisible = false;

      if (this.popover) {
        animate(this.popover, 0.3, { opacity: 0 }, () => {
          this.setState({ popoverVisible: false });
        });
      }
    }, 3000);
  };

  orderChangedHandler = () => {
    const { currentRoute } = this.props;
    const isResellerBundle = isResellerViewRoute(currentRoute);
    onUpdateCartNativeHandler(location.origin + getReviewRoute({ isReseller: isResellerBundle }));
  };

  showPopover = () => {
    if (this.isPopoverVisible) {
      this.startShutdownTimer();
      return;
    }

    this.setState({ popoverVisible: true }, () => {
      this.isPopoverVisible = false;

      if (!this.popover) {
        return;
      }

      animate(this.popover, 0.3, { opacity: 1 }, () => {
        this.isPopoverVisible = true;

        this.startShutdownTimer();
      });
    });
  };

  onClick = (event) => {
    if (!shouldProcessClick(event)) {
      return;
    }

    event.stopPropagation();
    event.preventDefault();
    const { currentRoute } = this.props;
    const { inquiryCount } = this.state;

    const isResellerBundle = isResellerViewRoute(currentRoute);
    clearCachedScrollPositions();
    route(getReviewRoute({ isReseller: isResellerBundle }));
    getOrder().then((orders) => {
      onReviewActionClick(
        { total_items: inquiryCount, order_id: orders.id },
        { callNativeHandler: isResellerBundle }
      );
    });
  };

  pulse = () => {
    if (this.animationStartTimer) {
      clearTimeout(this.animationStartTimer);
    }

    if (!this.state.pulse) {
      this.setState({ pulse: true });
    }

    if (this.animationEndTimer) {
      clearTimeout(this.animationEndTimer);
    }

    this.animationEndTimer = setTimeout(() => {
      this.setState({ pulse: false });
    }, 500);
  };

  getPopoverText = () => {
    const { inquiryCount } = this.state;

    return I18n.t(inquiryCount === 1 ? 'ITEM_IN_LIST_TOOLTIP' : 'ITEMS_IN_LIST_TOOLTIP', {
      count: inquiryCount
    });
  };

  render () {
    const { currentRoute } = this.props;
    const { isVisible, popoverVisible, inquiryCount, pulse } = this.state;
    const isResellerBundle = isResellerViewRoute(currentRoute);
    const theme = getCatalogueTheme();

    const iconColor = shouldShowLightThemeOnRoute(getCurrentRouteName())
      ? theme.themeTextColor
      : theme.homeGrid.searchClass === 'dark'
        ? '#fff'
        : theme.cartIconsColor;

    if (!isVisible) {
      return null;
    }

    return (
      <div className="nav-bar-review-action">
        <a
          href={getReviewRoute({ isReseller: isResellerBundle })}
          target="_self"
          title="view cart"
          className={`button ${pulse ? 'pulse' : ''}`}
          onClick={this.onClick}
        >
          <div
            className="text"
            style={
              inquiryCount
                ? {
                  paddingRight: '3px'
                }
                : {}
            }
          >
            <CartIcon color={iconColor} size={25} />
            {!!inquiryCount && <span className="count">{inquiryCount}</span>}
          </div>
        </a>

        {popoverVisible && (
          <div
            className="popover"
            ref={(element) => (this.popover = element)}
            style={{
              background: theme.homeGrid.backgroundColor
            }}
          >
            <div className="popover-arrow">
              <ArrowUpIcon size={15} color={theme.homeGrid.backgroundColor} />
            </div>

            <div
              className="icon"
              style={{
                background: theme.popOverIconColor
              }}
            >
              <img alt src={`${cdnHost}assets/checkmark.svg`} />
            </div>

            <div
              className="text"
              style={{
                color: theme.pageTextColor
              }}
            >
              {this.getPopoverText()}
            </div>
          </div>
        )}
      </div>
    );
  }
}

export default NavBarReviewAction;
